import React, { useEffect, useMemo, useState } from "react";
import { api, fmt } from "../../../services/erpApi";
import { StatCard, PageHeader, LoadingScreen, Modal, EmptyState } from "../../../components/admin/EssUi";
import { useToast } from "../../../components/admin/ToastContext";
import { Search, RefreshCw, Link2, CheckCircle2, Landmark, SquareCheck, FileText } from "lucide-react";

const AdminPanelReconciliationPage = () => {
  const toast = useToast();
  const [accounts, setAccounts] = useState([]);
  const [accountId, setAccountId] = useState("");
  const [accountMeta, setAccountMeta] = useState(null);
  const [lines, setLines] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState({});
  const [working, setWorking] = useState(false);
  const [showDone, setShowDone] = useState(null);

  useEffect(() => {
    (async () => {
      const accs = await api.accounts.list({ limit: 500, reconcile: true }).catch(() => []);
      setAccounts(accs || []);
      setLoaded(true);
    })();
  }, []);

  const loadLines = async (id) => {
    try {
      const d = await api.accounts.unreconciled(id);
      setLines((d?.lines || []).map((l) => ({
        ...l,
        move_id: l.move_id || l.move_parent_id || l.id,
      })));
      setAccountMeta(d ? { account_code: d.account_code, account_name: d.account_name } : null);
    } catch {
      setLines([]);
      setAccountMeta(null);
      toast("Could not load open items", "error");
    }
  };

  const changeAccount = (id) => {
    setAccountId(id);
    setSelected({});
    if (id) loadLines(id);
  };

  const moveIdOf = (l) => l.move_id;

  const filtered = useMemo(() => {
    if (!search.trim()) return lines;
    const q = search.toLowerCase();
    return lines.filter((l) =>
      `${l.partner_name || ""} ${l.ref || ""} ${l.label || ""}`.toLowerCase().includes(q)
    );
  }, [lines, search]);

  const toggle = (l) => {
    if (l.reconciled) return;
    const id = moveIdOf(l);
    setSelected((sel) => {
      if (sel[id]) {
        const next = { ...sel };
        delete next[id];
        return next;
      }
      return { ...sel, [id]: l };
    });
  };

  const selectedIds = Object.keys(selected);
  const selectedList = Object.values(selected);

  const handleReconcile = async () => {
    const [aId, bId] = selectedIds;
    setWorking(true);
    try {
      await api.accounting.reconcile(parseInt(aId), { counterpart_move_id: parseInt(bId) });
      setShowDone({ a: selected[aId], b: selected[bId] });
      toast("Moves reconciled", "success");
      setSelected({});
      await loadLines(accountId);
    } catch (e) {
      toast(e.message || "Reconciliation failed", "error");
    }
    setWorking(false);
  };

  const inputStyle = { width: "100%", padding: "9px 12px", border: "1px solid #cbd5e1", borderRadius: "6px", fontSize: "13.5px", outline: "none", background: "#fff", boxSizing: "border-box" };

  if (!loaded) return <LoadingScreen />;

  return (
    <div className="max-w-7xl mx-auto animate-fade-in" style={{ padding: "0 16px" }}>
      <PageHeader
        title="Reconciliation"
        subtitle="Match invoices and payments on third-party accounts"
        actions={
          selectedIds.length === 2 && (
            <button
              onClick={handleReconcile}
              disabled={working}
              className="ess-btn ess-btn-primary"
            >
              {working ? <RefreshCw size={16} className="animate-spin" /> : <Link2 size={16} />}
              Reconcile ({selectedIds.length})
            </button>
          )
        }
      />

      <div className="ess-sg ess-sg3" style={{ marginBottom: "24px" }}>
        <StatCard label="Reconcilable accounts" value={String(accounts.length)} icon={Landmark} color="indigo" trend="reconcile = true" />
        <StatCard label="Selected items" value={String(selectedIds.length)} icon={SquareCheck} color="green" trend="pick 2 to match" />
        <StatCard label="Open on account" value={String(lines.filter((l) => !l.reconciled).length)} icon={FileText} color="amber" trend={accountMeta ? `${accountMeta.account_code} · ${accountMeta.account_name}` : "choose an account"} />
      </div>

      <div style={{ display: "flex", gap: "12px", flexWrap: "wrap", marginBottom: "24px", background: "white", padding: "16px", borderRadius: "8px", border: "1px solid #e2e7e2" }}>
        <div style={{ flex: 1, minWidth: "260px" }}>
          <label style={{ fontSize: "13px", fontWeight: "600", color: "#334155", marginBottom: "6px", display: "block" }}>Account *</label>
          <select value={accountId} onChange={(e) => changeAccount(e.target.value)} style={inputStyle}>
            <option value="">Select a third-party account…</option>
            {accounts.map((a) => (
              <option key={a.id} value={a.id}>{a.code} — {a.name}</option>
            ))}
          </select>
        </div>
        <div style={{ flex: 2, minWidth: "220px", position: "relative" }}>
          <label style={{ fontSize: "13px", fontWeight: "600", color: "#334155", marginBottom: "6px", display: "block" }}>Filter</label>
          <Search size={16} style={{ position: "absolute", left: "12px", top: "36px", color: "#94a3b8" }} />
          <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search partner, ref, label…" style={{ ...inputStyle, paddingLeft: "36px" }} />
        </div>
      </div>

      <div style={{ background: "white", borderRadius: "8px", border: "1px solid #e2e7e2", overflow: "hidden" }}>
        {!accountId ? (
          <EmptyState icon="🏦" title="Select an account" subtitle="Open items of that account will be listed here" />
        ) : filtered.length === 0 ? (
          <EmptyState icon="✅" title="Nothing to reconcile" subtitle="All items are reconciled" />
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
              <thead>
                <tr>
                  <th className="ess-th" style={{ width: "40px", textAlign: "center" }}></th>
                  <th className="ess-th" style={{ minWidth: "110px" }}>Date</th>
                  <th className="ess-th" style={{ minWidth: "160px" }}>Partner</th>
                  <th className="ess-th" style={{ minWidth: "130px" }}>Ref</th>
                  <th className="ess-th" style={{ minWidth: "120px" }}>Label</th>
                  <th className="ess-th ess-th-r" style={{ minWidth: "100px" }}>Debit</th>
                  <th className="ess-th ess-th-r" style={{ minWidth: "100px" }}>Credit</th>
                  <th className="ess-th ess-th-r" style={{ minWidth: "100px" }}>Balance</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((l, index) => {
                  const isSel = !!selected[moveIdOf(l)];
                  const done = !!showDone && (showDone.a === l || showDone.b === l);
                  return (
                    <tr
                      key={`${l.id}-${index}`}
                      onClick={() => toggle(l)}
                      className="ess-tr"
                      style={{
                        background: isSel ? "#f0f7f0" : done ? "#f0fdf4" : "transparent",
                        cursor: l.reconciled ? "not-allowed" : "pointer",
                        opacity: l.reconciled ? 0.55 : 1,
                      }}
                    >
                      <td className="ess-td" style={{ textAlign: "center", padding: "10px 8px" }}>
                        {l.reconciled ? (
                          <CheckCircle2 size={17} style={{ color: "#16a34a" }} />
                        ) : (
                          <span style={{
                            display: "inline-block", width: "18px", height: "18px", borderRadius: "5px",
                            border: isSel ? "2px solid #225f2b" : "2px solid #cbd5e1",
                            background: isSel ? "#225f2b" : "white",
                          }} />
                        )}
                      </td>
                      <td className="ess-td" style={{ color: "#64748b", whiteSpace: "nowrap" }}>{fmt.date(l.date)}</td>
                      <td className="ess-td" style={{ fontWeight: "600", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{l.partner_name || "—"}</td>
                      <td className="ess-td" style={{ color: "#475569", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{l.ref || "—"}</td>
                      <td className="ess-td" style={{ color: "#64748b", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{l.label || "—"}</td>
                      <td className="ess-td" style={{ color: "#16a34a", textAlign: "right", whiteSpace: "nowrap" }}>{l.debit ? fmt.money(l.debit) : "—"}</td>
                      <td className="ess-td" style={{ color: "#be123c", textAlign: "right", whiteSpace: "nowrap" }}>{l.credit ? fmt.money(l.credit) : "—"}</td>
                      <td className="ess-td" style={{ fontWeight: "600", color: "#334155", textAlign: "right", whiteSpace: "nowrap" }}>{fmt.money(l.balance)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {selectedIds.length > 0 && (
          <div style={{ display: "flex", alignItems: "center", gap: "12px", justifyContent: "flex-end", padding: "12px 16px", borderTop: "1px solid #e2e7e2", background: "#fafafa" }}>
            <span style={{ fontSize: "13px", color: "#475569" }}>
              <strong>{selectedIds.length}</strong> selected
              {selectedIds.length === 2 && (
                <span style={{ color: "#64748b" }}>
                  {" "}· {fmt.money((Number(selectedList[0].balance) || 0) + (Number(selectedList[1].balance) || 0))} combined
                </span>
              )}
            </span>
            <button
              onClick={handleReconcile}
              disabled={working || selectedIds.length !== 2}
              className="ess-btn ess-btn-primary"
            >
              {working ? <RefreshCw size={15} className="animate-spin" /> : <Link2 size={15} />}
              Reconcile pair
            </button>
          </div>
        )}
      </div>

      <Modal open={!!showDone} onClose={() => setShowDone(null)} title="Reconciled" size="md">
        <div style={{ textAlign: "center", padding: "12px 0" }}>
          <CheckCircle2 size={44} style={{ color: "#16a34a", margin: "0 auto 12px" }} />
          <p style={{ fontSize: "14px", color: "#334155" }}>
            <strong>{showDone?.a?.ref || showDone?.a?.label || "Move"}</strong> ({fmt.money(showDone?.a?.balance)}) matched with{" "}
            <strong>{showDone?.b?.ref || showDone?.b?.label || "Move"}</strong> ({fmt.money(showDone?.b?.balance)}).
          </p>
          <p style={{ fontSize: "13px", color: "#64748b", marginTop: "6px" }}>Both moves are now settled.</p>
          <button
            onClick={() => setShowDone(null)}
            className="ess-btn ess-btn-primary"
            style={{ marginTop: "18px", padding: "10px 28px" }}
          >
            Done
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default AdminPanelReconciliationPage;
